import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@material-ui/core/Typography";

import { PnoSouscriptionContexte } from "views/PolicePno/Souscription";
import { BoutonSuivant } from "@/views/PolicePno/Souscription/BoutonSuivant";
import { ErreurFinscan } from "@/views/PolicePno/Souscription/Etapes/InfosPerso/ErreurFinscan";
import { RechercheProrio } from "./RechercheProrio";
import { InfosPersoForm } from "@/views/PolicePno/Souscription/Etapes/InfosPerso/Form";
import customerApi from "@/api/customerApi";
import { Personne } from "@/types/personne";
import { validerNumeroTel } from "@/utils/phone";
import { Souscription } from "@/types/Souscription";
import { useParams } from "react-router-dom";

export interface PJ {
  type?: string;
  valeur?: File | null;
}

type getTypeOriginalDepuisPartial<T> = T extends Partial<infer U> ? U : never;
type Contact = getTypeOriginalDepuisPartial<Souscription["data"]["contact"]>;
type ContactBooleanMap = { [K in keyof Contact]: boolean };

export const InfosPerso = () => {
  const { dataSouscription } = useContext(PnoSouscriptionContexte);
  const { t } = useTranslation();
  const { id } = useParams();

  const data = dataSouscription.data?.contact;

  const [erreurFinscan, setErreurFinscan] = useState<"ko" | "pending" | null>(
    null
  );
  const [champsDesactives, setChampsDesactives] = useState<
    ContactBooleanMap | {}
  >({});
  const contactInfoVide = () => (data ? Object.keys(data).length === 0 : true);
  const [popinAffichee, setPopinAffichee] = useState<boolean>(
    contactInfoVide()
  );
  const defaultPj = (): PJ | null =>
    dataSouscription.data?.typePj
      ? {
          type: dataSouscription.data?.typePj === "P" ? "P" : "CIN",
          valeur:
            dataSouscription.data?.typePj === "P"
              ? ({ name: "passport.pdf" } as File)
              : ({ name: "carteIdentite.pdf" } as File),
        }
      : null;
  const [pj, setPj] = useState<PJ | null>(defaultPj);

  const personneDejeCreee =
    dataSouscription.visitedSteps?.includes("INFOS_PERSO") || !!id;

  const skipCreationPersonne: () => Promise<string | null> = () =>
    new Promise((r) => r(null));

  const creePersonneEtVerifFinscan: () => Promise<string | null> = () =>
    new Promise(async (resolve, reject) => {
      const numeroTelInvalide = !validerNumeroTel(
        data?.phone.value || "",
        data?.phone?.country?.countryCode || ""
      );
      if (numeroTelInvalide) {
        return reject("Veuiller saisir un numéro de téléphone valide");
      }

      // call api finscan
      const finscanResponse = "ok";

      if (finscanResponse === "ok") {
        await customerApi
          .enregistreProprio(
            {
              ...data,
              gender: data?.gender,
              numFoncia: dataSouscription?.data?.numFoncia,
              phone: data?.phone?.value,
              typeJustificatifIdentite: pj?.type,
            } as Personne,
            pj?.valeur as File
          )
          .then(() => resolve(null))
          .catch(() =>
            reject(
              "Une erreur est survenue lors de l'enregistrement du proprietaire"
            )
          );
      } else if (finscanResponse === "pending") setErreurFinscan("pending");
      else setErreurFinscan("ko");
      return reject("Une erreur est survenue lors de la vérification Finscan");
    });

  return erreurFinscan === "pending" ? (
    <ErreurFinscan
      erreur={"pending"}
      retourPageInfo={() => setErreurFinscan(null)}
    />
  ) : erreurFinscan === "ko" ? (
    <ErreurFinscan erreur={"ko"} />
  ) : (
    <>
      <Typography variant="h6">{t("pno.infosProprio")}</Typography>
      <section>
        <InfosPersoForm
          pj={pj}
          setPj={setPj}
          champsDesactives={champsDesactives as ContactBooleanMap}
          affichePopin={() => setPopinAffichee(true)}
        />
        <div>
          <BoutonSuivant
            onClick={
              personneDejeCreee
                ? skipCreationPersonne
                : creePersonneEtVerifFinscan
            }
          />
        </div>
      </section>
      {popinAffichee && (
        <RechercheProrio
          onClose={() => setPopinAffichee(false)}
          setChampsDesactives={setChampsDesactives}
        />
      )}
    </>
  );
};
